import * as React from 'react';
import styled from 'styled-components';
import { colors } from './colors/color';
import { Button } from './button/Button';

const DEFAULT_WIDTH = '40%';
const ANIMATION_DURATION_MS = 300;

type DrawerProps = {
    visible: boolean,
    topMargin?:string,
    padding?:string,
    width?: string,
    onClose?: () => void,
    children: React.ReactNode,
}

export function Drawer(props: DrawerProps): JSX.Element {

    const [shown, setShown] = React.useState(false);

    React.useEffect(() => {
        if (props.visible) {
            const raf = requestAnimationFrame(() => {
                requestAnimationFrame(() => setShown(true));
            });
            return () => cancelAnimationFrame(raf);
        }

        setShown(false);
    }, [props.visible]);

    return (
        <Overlay $visible={shown}>
            <DrawerPanel $width={props.width ?? DEFAULT_WIDTH} $topMargin={props.topMargin ?? '42px'} $visible={shown}>
                {props.onClose &&
                    <DrawerCloseButtonRow>
                        <Button variant="text" icon="actions_close" size="32px" onClick={props.onClose} />
                    </DrawerCloseButtonRow>
                }
                <DrawerContent $padding={props.padding ?? '16px'} $hasCloseButton={!!props.onClose}>
                    {props.children}
                </DrawerContent>
            </DrawerPanel>
        </Overlay>
    );
}

const Overlay = styled.div<{ $visible: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    background-color: rgba(120, 120, 120, 0.35);
    opacity: ${props => props.$visible ? 1 : 0};
    transition: opacity ${ANIMATION_DURATION_MS}ms ease, visibility 0s ${props => props.$visible ? '0s' : `${ANIMATION_DURATION_MS}ms`};
    pointer-events: ${props => props.$visible ? 'auto' : 'none'};
    visibility: ${props => props.$visible ? 'visible' : 'hidden'};
`;

const DrawerPanel = styled.div<{ $width: string, $topMargin: string, $visible: boolean }>`
    width: ${props => props.$width};
    height: calc(100% - ${props => props.$topMargin});
    margin-top: ${props => props.$topMargin};
    box-sizing: border-box;
    background: ${colors.modal.background};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transform: ${props => props.$visible ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform ${ANIMATION_DURATION_MS}ms ease;
`;

const DrawerCloseButtonRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
    padding: 4px 4px 4px 4px;
    background: ${colors.modal.background};
`;

const DrawerContent = styled.div<{ $padding: string, $hasCloseButton: boolean }>`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: ${props => props.$hasCloseButton ? `0 ${props.$padding} ${props.$padding}` : props.$padding};
`;
